import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IoAlertCircleOutline,
  IoCalendarClearOutline,
  IoCalendarOutline,
  IoCashOutline,
  IoCheckmarkCircleOutline,
  IoTimeOutline,
} from "react-icons/io5";
import { getLocale } from "../../../i18n/config";
import GetData from "../../../services/getData";
import logPatientChange from "../../../services/patientLogs";
import { buildPatientLogText } from "../../../services/patientLogMessages";
import "./PatientPlan.css";
import { Button, SegmentedControl } from "../../../components/ui";
import DateInput from "../../../components/ui/DateInput";
import Toast from "../../../components/layout/toast/Toast";
import { Modal, Form } from "react-bootstrap";
import SearchSelectModal from "../../../components/layout/searchSelectModal/SearchSelectModal";
import PlanTabs from "./PlanTabs";
import EditableModal from "../../../components/layout/editableModal/EditableModal";
import PaidModal from "./PaidModal";
import PatientPlanMatrix from "./PatientPlanMatrix";
import PatientPlanTable from "./PatientPlanTable";
import PatientPlanTimeline from "./PatientPlanTimeline";
import {
  buildAppointmentTypeMap,
  getStageCellLabels,
  getWeekDaysShort,
} from "./planStageLabels";
import { useUserSettings } from "../../../context/UserSettingsContext";
import { localizedSuffix } from "../../../i18n/localizedService";

/**
 * Название услуги с приоритетом языка интерфейса.
 *
 * Записи плана приходят с полями `name` (русское) и `name_en` (английский
 * перевод). На en/ar показываем name_en, но если перевод не заполнен —
 * русское название. На ru — наоборот.
 */
const pickLocalizedValue = (record, key, language) => {
  const suffix = localizedSuffix(language);
  const primary = record?.[`${key}${suffix}`]?.value;
  if (primary != null && String(primary).trim() !== "") return primary;

  const secondary = record?.[suffix ? key : `${key}_en`]?.value;
  if (secondary != null && String(secondary).trim() !== "") return secondary;

  return "";
};

const ALLERGY_ANAMNESIS_KEYS = [
  "medications",
  "cosmetics",
  "food",
  "household",
  "plants",
  "sun",
  "cold",
];

const ALLERGY_ANAMNESIS_FIELD_KEYS = {
  medications: "plan.allergy.medications",
  cosmetics: "plan.allergy.cosmetics",
  food: "plan.allergy.food",
  household: "plan.allergy.household",
  plants: "plan.allergy.plants",
  sun: "plan.allergy.sun",
  cold: "plan.allergy.cold",
};

const parseAllergyAnamnesisRaw = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
  } catch (_) {
    return null;
  }
  return null;
};

const formatAllergyAnamnesisLine = (raw, t) => {
  const source = parseAllergyAnamnesisRaw(raw);
  if (!source) return "";
  return ALLERGY_ANAMNESIS_KEYS.map((key) => {
    const item = source[key];
    if (!item || typeof item !== "object" || Array.isArray(item)) return "";
    if (item.answer !== "yes") return "";
    const note = String(item.note ?? "")
      .replace(/\s+/g, " ")
      .trim();
    const label = t(ALLERGY_ANAMNESIS_FIELD_KEYS[key]);
    return note ? `${label} (${note})` : label;
  })
    .filter(Boolean)
    .join("; ");
};

function PatientPlan() {
  const { t, i18n } = useTranslation("patient");
  const locale = getLocale(i18n.language);
  // Отображаемое название услуги: язык интерфейса, при пустом переводе — другой язык.
  const displayName = (record) => pickLocalizedValue(record, "name", i18n.language);
  const { get, update } = useUserSettings();
  const [loading, setLoading] = useState(true); // только для первичной загрузки/смены пациента
  const [error, setError] = useState(null);
  const { patientId, planId: routePlanId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { planGroupsSlotEl } = useOutletContext() || {};
  const [patient, setPatient] = useState(null);
  const [allergyAnamnesisRaw, setAllergyAnamnesisRaw] = useState(null);
  const allergyAnamnesisLine = React.useMemo(
    () => formatAllergyAnamnesisLine(allergyAnamnesisRaw, t),
    [allergyAnamnesisRaw, t],
  );
  const [changed, setChanged] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [toast, setToastState] = useState({});
  // Каждое новое сообщение получает свой id. Компонент Toast рисует
  // уведомление по key={toast.id}, поэтому одинаковый текст показывается
  // повторно. CSS-анимация сама убирает его через ~3.4 с.
  const toastSeqRef = useRef(0);
  const setToast = React.useCallback((next) => {
    toastSeqRef.current += 1;
    // По умолчанию уведомление зелёное: почти все вызовы без явного status —
    // это успешно выполненные операции с текстом от сервера. Ошибки всегда
    // передают status: "error".
    setToastState({
      status: "success",
      ...(next || {}),
      id: toastSeqRef.current,
    });
  }, []);

  // Чтобы при перерендере (после запроса) не "прыгало" вверх: сохраняем и восстанавливаем scrollY
  const restoreScrollPendingRef = useRef(false);
  const lastScrollYRef = useRef(0);
  // Автовход в режим редактирования (?edit=1) выполняется один раз для конкретного плана
  const autoEditAppliedRef = useRef(null);

  const [showModalServices, setShowModalServices] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedGroupType, setSelectedGroupType] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [serviceSelectMode, setServiceSelectMode] = useState("add_service"); // add_service | add_subitem
  const [selectedParentItemId, setSelectedParentItemId] = useState(null);
  const [serviceSearchQuery, setServiceSearchQuery] = useState("");
  const [serviceSearchLoading, setServiceSearchLoading] = useState(false);
  const [serviceSearchResults, setServiceSearchResults] = useState([]);
  const [serviceSearchError, setServiceSearchError] = useState(null);

  // Фильтр услуг по филиалу в модальном окне выбора услуги.
  const [serviceFilials, setServiceFilials] = useState([]);
  const [serviceMyClinics, setServiceMyClinics] = useState([]);
  const [serviceClinicId, setServiceClinicId] = useState("");

  const [loadingGroup, setLoadingGroup] = useState(false);

  const [loadingService, setLoadingService] = useState(false);
  const [showModalService, setShowService] = useState(false);
  const [paramsService, setParamsService] = useState([]);

  const [showModalPay, setShowModalPay] = useState(false);
  const [availableClinics, setAvailableClinics] = useState([]);
  const [payModalStaff, setPayModalStaff] = useState([]);
  const [paymentGroupId, setPaymentGroupId] = useState(null);

  // Режим отображения курсов
  const [coursesMode, setCoursesMode] = useState(false);
  // Выбор услуг для курсов (чекбоксы по услугам)
  const [courseSelected, setCourseSelected] = useState([]);
  const [courseSelectedName, setCourseSelectedName] = useState("");
  const [courseSelectedColor, setCourseSelectedColor] = useState("#fffae0");
  const [showModalAddCourse, setShowModalAddCourse] = useState(false);

  // Раскрывающееся меню «Добавить услугу»: из каталога / новую.
  // Храним id группы, для которой меню открыто (null — закрыто).
  const [addServiceMenuGroupId, setAddServiceMenuGroupId] = useState(null);
  // Номер приёма, к которому нужно привязать услугу, добавленную из окна
  // приёма в схеме лечения (null — обычное добавление в группу).
  const [pendingStageNumber, setPendingStageNumber] = useState(null);
  // Модальное окно добавления своей (не из каталога) услуги в план лечения
  const [showModalCustomService, setShowModalCustomService] = useState(false);
  const [customServiceName, setCustomServiceName] = useState("");
  // Цена новой услуги — вводится в том же окне, чтобы не делать второй шаг.
  const [customServicePrice, setCustomServicePrice] = useState("");
  const [customServiceContext, setCustomServiceContext] = useState({
    groupType: null,
    groupId: null,
    // Номер приёма, если услуга добавляется из схемы лечения
    // (null — обычное добавление в направление).
    appointmentNumber: null,
  });

  // Закрытие меню «Добавить услугу» по клику вне него
  useEffect(() => {
    if (addServiceMenuGroupId === null) return undefined;
    const handleClickOutside = (event) => {
      if (!event.target.closest(".btn-add-service-dropdown")) {
        setAddServiceMenuGroupId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [addServiceMenuGroupId]);

  // Название плана лечения
  const [planName, setPlanName] = useState("");
  const [treatmentStartDate, setTreatmentStartDate] = useState("");

  // Предустановленные цвета для курса (HTML-формат #RRGGBB, очень бледные)
  const COURSE_COLOR_OPTIONS = [
    { labelKey: "plan.colors.paleYellow", value: "#fffae0" },
    { labelKey: "plan.colors.paleGreen", value: "#eaf9ea" },
    { labelKey: "plan.colors.paleBlue", value: "#d4ffff" },
    { labelKey: "plan.colors.palePink", value: "#ffdae0" },
    { labelKey: "plan.colors.paleOrange", value: "#ffeacb" },
    { labelKey: "plan.colors.paleLavender", value: "#e6e6fa" },
    { labelKey: "plan.colors.paleMint", value: "#056a0545" },
    { labelKey: "plan.colors.palePeach", value: "#ffdab996" },
    { labelKey: "plan.colors.paleSky", value: "#aadbf999" },
    { labelKey: "plan.colors.paleLilac", value: "#c793f77a" },
  ];

  const WITHOUT_APPOINTMENTS_GROUP_NAMES = new Set([
    "пероральные лекарственные препараты",
    "процедуры",
    "чекапы",
  ]);

  const weekDaysShort = React.useMemo(() => getWeekDaysShort(t), [t]);

  // Названия этапов программы (типы приёмов) по номеру приёма — на уровне плана.
  const appointmentTypeMap = React.useMemo(
    () => buildAppointmentTypeMap(patient?.result?.appointment_types, i18n.language),
    [patient?.result?.appointment_types, i18n.language],
  );

  // Сохранённые названия типов (ru/en) по номеру приёма — для сравнения при
  // смене типа: подпись в интерфейсе локализована, а в плане хранятся ru/en.
  const appointmentTypeNamesMap = React.useMemo(() => {
    const map = {};
    (patient?.result?.appointment_types || []).forEach((type) => {
      const number = parseInt(type?.appointment_number?.value, 10);
      if (!Number.isFinite(number) || number <= 0) return;
      map[number] = {
        name: String(type?.name?.value ?? "").trim(),
        nameEn: String(type?.name_en?.value ?? "").trim(),
      };
    });
    return map;
  }, [patient?.result?.appointment_types]);

  const normalizeGroupName = (name) =>
    String(name || "")
      .trim()
      .toLowerCase();

  const isWithoutAppointmentsGroupByName = (name) =>
    WITHOUT_APPOINTMENTS_GROUP_NAMES.has(normalizeGroupName(name));

  const getItemBasePriceValue = (item) =>
    parseFloat(item?.amount_base?.value ?? item?.amount_base_total?.value ?? 0) || 0;

  const getGroupEffectiveTotal = (group) => {
    const backendTotal = parseFloat(group?.amount_total?.value);
    if (!isWithoutAppointmentsGroupByName(group?.name?.value)) {
      return Number.isFinite(backendTotal) ? backendTotal : 0;
    }

    return (group?.items || []).reduce(
      (sum, item) => sum + getItemBasePriceValue(item),
      0,
    );
  };

  const planEffectiveTotal = (patient?.result?.groups || []).reduce(
    (sum, group) => sum + getGroupEffectiveTotal(group),
    0,
  );
  const planTotalToDisplay =
    (patient?.result?.groups || []).length > 0
      ? planEffectiveTotal
      : parseFloat(patient?.result?.total_calculated_price?.value) || 0;

  // Модальное окно «Указать дату приёма»
  const [showSetDateModal, setShowSetDateModal] = useState(false);
  const [setDateParams, setSetDateParams] = useState({ appointmentNumber: null, groupId: null });
  const [showAppointmentCommentModal, setShowAppointmentCommentModal] = useState(false);
  const [appointmentCommentParams, setAppointmentCommentParams] = useState({
    appointmentNumber: null,
    groupId: null,
    itemId: null,
  });
  const [appointmentCommentValue, setAppointmentCommentValue] = useState("");
  const [showAppointmentCommentViewModal, setShowAppointmentCommentViewModal] = useState(false);
  const [appointmentCommentViewValue, setAppointmentCommentViewValue] = useState("");

  // Вид плана: «Детально» (таблица, по умолчанию), «Схема» (матрица)
  // или «Таймлайн» (этапы приёмов).
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem("mis:plan-view");
      if (saved === "matrix" || saved === "timeline") return saved;
      return "table";
    } catch (_) {
      return "table";
    }
  });

  const changeViewMode = (nextMode) => {
    // В схеме-матрице и таймлайне режима редактирования нет, поэтому при
    // переключении на них из режима редактирования таблицы сохраняем изменения
    // автоматически: фиксируем поля, которые сохраняются по потере фокуса
    // (название плана, дата начала лечения), и выходим из режима правки.
    // Остальные изменения уходят на сервер сразу в момент действия.
    if (nextMode !== "table" && changed) {
      if (typeof document !== "undefined") {
        const active = document.activeElement;
        if (active && typeof active.blur === "function") active.blur();
      }
      setChanged(false);
      setToast({ title: t("plan.toast.changesSaved"), status: "success" });
    }

    setViewMode(nextMode);
    try {
      localStorage.setItem("mis:plan-view", nextMode);
    } catch (_) {
      /* localStorage может быть недоступен */
    }
  };

  // Что РЕАЛЬНО нарисовано. В режиме редактирования схема-матрица не
  // показывается никогда — всегда таблица («Группа»). Поэтому переключатель
  // должен указывать на «Группу», иначе горит «Схема», а на экране таблица
  // (так было после «Добавить из шаблона»: переход с ?edit=1 включал правку).
  // viewMode при этом не меняется — сохранённый выбор вида (Схема/Группа)
  // остаётся, и после сохранения изменений пользователь снова видит свой вид.
  const activeViewMode = changed ? "table" : viewMode;


  const fields = [
    { name: "name", label: t("plan.fields.name") },
    { name: "sort", label: t("plan.fields.sort"), type: "number" },
    { name: "amount_base", label: t("plan.fields.amountBase"), type: "number" },
    { name: "composition", label: t("plan.fields.composition"), type: "textarea" },
    { name: "technology", label: t("plan.fields.technology"), type: "textarea" },
  ];

  const isSuccessResponse = (data) =>
    Boolean(data) && data?.status !== "error" && data?.status !== "fail";

  const getCurrentPlanId = () => {
    const parsedRoutePlanId = Number(routePlanId);
    if (Number.isFinite(parsedRoutePlanId) && parsedRoutePlanId > 0) {
      return parsedRoutePlanId;
    }
    const fallbackPlanId = Number(patient?.result?.id?.value);
    return Number.isFinite(fallbackPlanId) && fallbackPlanId > 0 ? fallbackPlanId : 0;
  };

  const refreshCurrentPlan = async () => {
    const currentPlanId = getCurrentPlanId();
    if (!currentPlanId) return null;
    try {
      const refreshedPlan = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: currentPlanId,
      });
      if (isSuccessResponse(refreshedPlan)) {
        setPatient(refreshedPlan);
        return refreshedPlan;
      }
      return null;
    } catch (err) {
      console.error("Ошибка перезагрузки текущего плана:", err);
      return null;
    }
  };

  const applyPlanResponse = async (data) => {
    if (!data || !data.result) return null;

    const parsedRoutePlanId = Number(routePlanId);
    const hasRoutePlanId = Number.isFinite(parsedRoutePlanId) && parsedRoutePlanId > 0;
    const responsePlanId = Number(data?.result?.id?.value);
    const hasResponsePlanId = Number.isFinite(responsePlanId) && responsePlanId > 0;

    if (hasRoutePlanId && hasResponsePlanId && parsedRoutePlanId !== responsePlanId) {
      const refreshedPlan = await refreshCurrentPlan();
      if (refreshedPlan) return refreshedPlan;
    }

    setPatient(data);
    return data;
  };

  const logPlanChange = async (
    key,
    { idItem = "", type = "", idClinic = 0, details = "" } = {},
  ) => {
    await logPatientChange({
      idPatient: patientId,
      idItem,
      type,
      idClinic,
      text: buildPatientLogText(key, { details }),
    });
  };

  const getServiceNameByItemId = (itemId) => {
    const allItems = (patient?.result?.groups || []).flatMap((group) => group?.items || []);
    const found = allItems.find((item) => String(item?.id?.value) === String(itemId));
    return found?.name?.value || "";
  };

  const getGroupNameById = (groupId) => {
    const found = (patient?.result?.groups || []).find(
      (group) => String(group?.id?.value) === String(groupId),
    );
    return found?.name?.value || "";
  };

  const getGroupAppointmentLimit = (group) => {
    const groupLimit = Number(group?.count_appointment?.value);
    if (Number.isFinite(groupLimit) && groupLimit > 0) {
      return groupLimit;
    }
    const planLimit = Number(patient?.result?.count_appointment?.value);
    return Number.isFinite(planLimit) && planLimit > 0 ? planLimit : 0;
  };

  const getAppointmentHeaderType = (group) => {
    const groupType = Number(group?.column_type?.value);
    if (Number.isFinite(groupType) && groupType >= 0 && groupType <= 4) {
      return groupType;
    }
    return 0;
  };

  // Две независимые подписи столбца: сверху крупно — номер приёма / день недели
  // по режиму группы (column_type), снизу бледно — тип приёма (если задан).
  const getAppointmentHeaderLabels = (appointmentNumber, appointmentHeaderType) =>
    getStageCellLabels({
      appointmentNumber,
      typeMap: appointmentTypeMap,
      columnType: appointmentHeaderType,
      weekDaysShort,
      t,
    });

  const handleColumnTypeChange = async (group, nextTypeRaw) => {
    const groupId = Number(group?.id?.value) || 0;
    const nextType = Number(nextTypeRaw);
    if (!groupId) return;
    if (!Number.isFinite(nextType) || nextType < 0 || nextType > 4) return;
    if (blocked) return;

    const currentType = getAppointmentHeaderType(group);
    if (currentType === nextType) return;

    setBlocked(true);
    try {
      await fetchPlan({
        id_plan: patient?.result?.id?.value || 0,
        id_group: groupId,
        action: "column_type_group",
        value: nextType,
        showFullLoading: false,
      });
    } finally {
      setBlocked(false);
    }
  };

  useEffect(() => {
    const parsedPlanId = Number(routePlanId);
    fetchPlan({
      id_plan: Number.isFinite(parsedPlanId) && parsedPlanId > 0 ? parsedPlanId : 0,
      showFullLoading: true,
    });
  }, [patientId, routePlanId]);

  // Автоматический вход в режим редактирования, если план открыт после создания (?edit=1).
  useEffect(() => {
    if (searchParams.get("edit") !== "1") return;
    if (!patient || !isSuccessResponse(patient)) return;

    const loadedPlanId = Number(patient?.result?.id?.value) || 0;
    const targetPlanId = Number(routePlanId) || loadedPlanId;
    if (autoEditAppliedRef.current === targetPlanId) return;

    autoEditAppliedRef.current = targetPlanId;
    setCoursesMode(false);
    setChanged(true);

    // Убираем параметр из URL, чтобы он не навязывал режим при обновлении/переходах.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("edit");
    setSearchParams(nextParams, { replace: true });
  }, [patient, routePlanId, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    const loadAllergyAnamnesis = async () => {
      setAllergyAnamnesisRaw(null);
      if (!patientId) return;
      try {
        const docsListData = await GetData({
          module: "patient",
          block: "patient_docs",
          id_patient: patientId,
          action: "list",
        });
        const docs = Array.isArray(docsListData?.result) ? docsListData.result : [];
        const latestAnamnesis = docs
          .filter((doc) => Number(doc?.template_id) === 3)
          .sort((a, b) => {
            const da = Date.parse(a?.update_date || a?.created_date) || 0;
            const db = Date.parse(b?.update_date || b?.created_date) || 0;
            if (db !== da) return db - da;
            return (Number(b?.id) || 0) - (Number(a?.id) || 0);
          })[0];
        if (cancelled) return;
        setAllergyAnamnesisRaw(latestAnamnesis?.data?.allergy_anamnesis ?? null);
      } catch (err) {
        console.error("Ошибка загрузки аллергического анамнеза:", err);
      }
    };

    loadAllergyAnamnesis();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  useLayoutEffect(() => {
    if (!restoreScrollPendingRef.current) return;
    restoreScrollPendingRef.current = false;

    if (typeof window === "undefined") return;
    const y = lastScrollYRef.current || 0;

    // 2 кадра — чтобы дождаться реального layout после обновления таблицы
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, y);
      });
    });
  }, [patient]);

  const handleChangedOpen = () => {
    // При входе в режим редактирования выходим из режима курсов
    setCoursesMode(false);
    setChanged(true);
  };
  const handleChangedClose = () => {
    setChanged(false);
  };

  const handleCoursesOpen = () => {
    // При входе в режим курсов выходим из режима редактирования
    setChanged(false);
    setCoursesMode(true);
  };

  const handleCoursesClose = () => {
    setCoursesMode(false);
  };

  const handleToggleCourseItem = (itemId) => {
    setCourseSelected((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  /** Удаление услуги из курса. Аргумент: id услуги (service_id). */
  const handleRemoveItemFromCourse = async (itemId) => {
    if (blocked) return;
    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_treatment_plan: patient?.result?.id?.value || 0,
        action: "remove_item_from_course",
        service_id: itemId,
      });
      setToast({
        title: data?.message || t("plan.toast.courseItemRemoved"),
        status: data?.status || "success",
      });
      if (data?.status !== "error") {
        await logPlanChange("patient_plan_course_item_removed", {
          idItem: itemId,
          type: "remove_item_from_course",
          details: t("plan.log.serviceId", { id: itemId }),
        });
        await applyPlanResponse(data);
      }
    } catch (err) {
      console.error("Ошибка удаления услуги из курса:", err);
      setToast({
        title: t("plan.toast.courseItemRemoveFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  /** Полное удаление услуги из плана лечения */
  const handleDeleteService = async (serviceItemId) => {
    if (blocked) return;
    if (!window.confirm(t("plan.toast.confirmDeleteService"))) return;
    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        action: "delete_service",
        service_id: serviceItemId,
      });
      setToast({
        title: data?.message || t("plan.toast.serviceDeleted"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_service_deleted", {
          idItem: serviceItemId,
          type: "delete_service",
          details: getServiceNameByItemId(serviceItemId) || t("plan.log.serviceId", { id: serviceItemId }),
        });
        await applyPlanResponse(data);
      }
    } catch (err) {
      console.error("Ошибка удаления услуги:", err);
      setToast({
        title: t("plan.toast.serviceDeleteFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  /** Удаление подуслуги из состава услуги */
  const handleDeleteSubitem = async (subitemId) => {
    if (blocked) return;
    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        action: "delete_subitem",
        id_subitem: subitemId,
      });
      setToast({
        title: data?.message || t("plan.toast.subitemDeleted"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_subitem_deleted", {
          idItem: subitemId,
          type: "delete_subitem",
          details: t("plan.log.subitemId", { id: subitemId }),
        });
        await applyPlanResponse(data);
      }
    } catch (err) {
      console.error("Ошибка удаления подуслуги:", err);
      setToast({
        title: t("plan.toast.subitemDeleteFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  const handleOpenModalAddCourse = () => {
    setShowModalAddCourse(true);
  };

  const handleSubmitAddCourse = async () => {
    if (blocked) return;
    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_treatment_plan: patient?.result?.id?.value || 0,
        action: "add_course",
        course_name: courseSelectedName,
        course_color: courseSelectedColor,
        item_ids: courseSelected,
      });
      setToast({
        title: data?.message || t("plan.toast.courseAdded"),
        status: data?.status || "success",
      });
      if (data?.status !== "error") {
        await logPlanChange("patient_plan_course_added", {
          type: "add_course",
          details: t("plan.log.courseDetails", {
            name: courseSelectedName || t("plan.log.courseNoName"),
            count: courseSelected.length,
          }),
        });
        const parsedRoutePlanId = Number(routePlanId);
        const currentPlanId =
          Number.isFinite(parsedRoutePlanId) && parsedRoutePlanId > 0
            ? parsedRoutePlanId
            : patient?.result?.id?.value || 0;
        try {
          const refreshedPlan = await GetData({
            module: "patient",
            block: "treatment_plan",
            id_patient: patientId,
            id_plan: currentPlanId,
          });
          if (isSuccessResponse(refreshedPlan)) {
            setPatient(refreshedPlan);
          } else {
            await applyPlanResponse(data);
          }
        } catch (refreshErr) {
          console.error("Ошибка перезагрузки текущего плана после добавления курса:", refreshErr);
          await applyPlanResponse(data);
        }
        setShowModalAddCourse(false);
        setCourseSelected([]);
        setCourseSelectedName("");
        setCourseSelectedColor(COURSE_COLOR_OPTIONS[0]?.value ?? "#fffae0");
      }
    } catch (err) {
      console.error("Ошибка добавления курса:", err);
      setToast({
        title: t("plan.toast.courseAddFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  /** Удаление группы (со всеми её услугами) */
  const handleDeleteGroup = async (groupId) => {
    if (blocked) return;
    if (!window.confirm(t("plan.toast.confirmDeleteGroup"))) return;
    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        id_group: groupId,
        action: "delete_group",
      });
      setToast({
        title: data?.message || t("plan.toast.groupDeleted"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_group_deleted", {
          idItem: groupId,
          type: "delete_group",
          details: getGroupNameById(groupId) || t("plan.log.groupId", { id: groupId }),
        });
        await applyPlanResponse(data);
      }
    } catch (err) {
      console.error("Ошибка удаления группы:", err);
      setToast({
        title: t("plan.toast.groupDeleteFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  /** Полное удаление плана лечения */
  const handleDeletePlan = async () => {
    if (blocked) return;
    const currentPlanId = getCurrentPlanId();
    if (!currentPlanId) return;
    if (!window.confirm(t("plan.toast.confirmDeletePlan"))) return;
    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: currentPlanId,
        action: "delete_plan",
      });
      setToast({
        title: data?.message || t("plan.toast.planDeleted"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        navigate(`/patient/${patientId}/plan`);
      }
    } catch (err) {
      console.error("Ошибка удаления плана:", err);
      setToast({
        title: t("plan.toast.planDeleteFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  const fetchPlan = async ({
    id_plan = 0,
    id_group = 0,
    id_item = 0,
    id_appointment = 0,
    action = "",
    value = null,
    comment = "",
    service_id = null,
    group_type = null,
    appointment_number = 0,
    appointment_type_name = "",
    appointment_type_name_en = "",
    showFullLoading = false,
    preserveScroll = true,
    silent = false,
  } = {}) => {
    if (preserveScroll && typeof window !== "undefined") {
      lastScrollYRef.current = window.scrollY || 0;
      restoreScrollPendingRef.current = true;
    } else {
      restoreScrollPendingRef.current = false;
    }

    if (showFullLoading) setLoading(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan,
        id_group,
        id_item,
        id_appointment,
        action,
        value,
        comment,
        service_id,
        group_type,
        appointment_number,
        appointment_type_name,
        appointment_type_name_en,
      });
      await applyPlanResponse(data);
      console.log("data", data);
      // Сообщение сервера НЕ показываем на каждый запрос: у промежуточных
      // шагов ответ «Сохранено» всплывал даже когда видимых изменений не
      // было (например, add_appointment поднимал только лимит плана).
      // Тост теперь показывает конкретный обработчик — по проверенному
      // результату. Здесь остаются только ошибки и многошаговые действия,
      // которым нужен текст сервера (они передают silent: true и говорят сами).
      if (!silent && !isSuccessResponse(data)) {
        setToast({
          title: data?.message || t("plan.toast.loadFailed"),
          status: "error",
        });
      }
      setBlocked(false);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      if (showFullLoading) setLoading(false);
    }
  };

  const handleIncrease = async (group) => {
    const groupId = group?.id?.value || 0;
    if (!groupId) return;
    if (blocked) return;
    setBlocked(true);
    try {
      const response = await fetchPlan(
        {
          id_plan: patient?.result?.id?.value || 0,
          id_group: groupId,
          action: "count_appointment_group",
          value: "plus",
          showFullLoading: false,
        }
      );
      if (isSuccessResponse(response)) {
        const responseGroup = (response?.result?.groups || []).find(
          (g) => String(g?.id?.value) === String(groupId),
        );
        await logPlanChange("patient_plan_appointment_count_increased", {
          idItem: groupId,
          type: "count_appointment",
          details: `${group?.name?.value || t("plan.log.groupFallback", { id: groupId })}: ${responseGroup?.count_appointment?.value ?? "—"}`,
        });
      }
    } finally {
      setBlocked(false);
    }
  };

  const handleDecrease = async (group) => {
    const groupId = group?.id?.value || 0;
    if (!groupId) return;
    if (blocked) return;
    setBlocked(true);
    try {
      const response = await fetchPlan(
        {
          id_plan: patient?.result?.id?.value || 0,
          id_group: groupId,
          action: "count_appointment_group",
          value: "minus",
          showFullLoading: false,
        }
      );
      if (isSuccessResponse(response)) {
        const responseGroup = (response?.result?.groups || []).find(
          (g) => String(g?.id?.value) === String(groupId),
        );
        await logPlanChange("patient_plan_appointment_count_decreased", {
          idItem: groupId,
          type: "count_appointment",
          details: `${group?.name?.value || t("plan.log.groupFallback", { id: groupId })}: ${responseGroup?.count_appointment?.value ?? "—"}`,
        });
      }
    } finally {
      setBlocked(false);
    }
  };

  // Число этапов плана: максимум из лимита плана и лимитов групп
  // (у плана count_appointment может быть 0 — «индивидуально по группам»).
  const getStageCount = (data) => {
    const planCount = Number(data?.result?.count_appointment?.value) || 0;
    const groupCounts = (data?.result?.groups || []).map(
      (group) => Number(group?.count_appointment?.value) || 0,
    );
    return Math.max(planCount, ...groupCounts, 0);
  };

  // Сколько приёмов (столбцов) РЕАЛЬНО видно в интерфейсе. Схема и таблица
  // строятся по лимитам групп (лимит плана — только если групп нет), плюс
  // учитываются уже расставленные назначения. Отличается от getStageCount:
  // там берётся максимум с лимитом плана, и при плане 10 / группах 6 прирост
  // лимита плана считался успехом, хотя новых столбцов не появлялось.
  const getVisibleStageCount = (data) => {
    let max = 0;
    (data?.result?.groups || []).forEach((group) => {
      const groupLimit = Number(group?.count_appointment?.value) || 0;
      if (groupLimit > max) max = groupLimit;
      (group?.items || []).forEach((item) => {
        (item?.appointments || []).forEach((appointment) => {
          const number = parseInt(appointment?.appointment_number?.value, 10);
          if (Number.isFinite(number) && number > max) max = number;
        });
      });
    });
    if (!max) max = Number(data?.result?.count_appointment?.value) || 0;
    return max;
  };

  // Добавление ещё одного приёма в конец плана (крайний столбец схемы).
  // Тип приёма (название этапа) можно передать сразу — его выбирают в меню
  // кнопки «+ Добавить ⌄», тогда новый столбец сразу получает подпись.
  // Основной путь — один запрос `add_appointment`. Если сервер его ещё не
  // знает (старый бэкенд не перезапущен/не обновлён), приём до-добавляется
  // существующим действием `count_appointment_group` по каждой группе.
  const handleAddStage = async (typeName = "", typeNameEn = "") => {
    const currentPlanId = getCurrentPlanId();
    if (!currentPlanId || blocked) return;
    const stageTypeName = String(typeName || "").trim();
    const stageTypeNameEn = String(typeNameEn || "").trim();
    // Проверяем прирост по ВИДИМЫМ столбцам, а не по лимиту плана.
    const currentCount = getVisibleStageCount(patient);
    setBlocked(true);
    try {
      let lastData = await fetchPlan({
        id_plan: currentPlanId,
        action: "add_appointment",
        appointment_type_name: stageTypeName,
        appointment_type_name_en: stageTypeNameEn,
        showFullLoading: false,
        silent: true,
      });

      if (getVisibleStageCount(lastData) <= currentCount) {
        const groupIds = (patient?.result?.groups || [])
          .map((group) => Number(group?.id?.value) || 0)
          .filter((id) => id > 0);
        for (const groupId of groupIds) {
          lastData = await fetchPlan({
            id_plan: currentPlanId,
            id_group: groupId,
            action: "count_appointment_group",
            value: "plus",
            showFullLoading: false,
            silent: true,
          });
        }
      }

      const nextCount = getVisibleStageCount(lastData);
      if (nextCount <= currentCount) {
        setToast({ title: t("plan.toast.stageAddFailed"), status: "error" });
        return;
      }

      // Старый бэкенд не знает про тип в `add_appointment` — ставим его
      // отдельным запросом, чтобы подпись нового столбца не потерялась.
      if (stageTypeName || stageTypeNameEn) {
        const hasType = (lastData?.result?.appointment_types || []).some(
          (row) => Number(row?.appointment_number?.value) === nextCount,
        );
        if (!hasType) {
          lastData = await fetchPlan({
            id_plan: currentPlanId,
            action: "set_appointment_type",
            appointment_number: nextCount,
            appointment_type_name: stageTypeName,
            appointment_type_name_en: stageTypeNameEn,
            showFullLoading: false,
            silent: true,
          });
        }
      }

      setToast({
        title: t("plan.toast.stageAdded", { number: nextCount }),
        status: "success",
      });

      await logPlanChange("patient_plan_appointment_count_increased", {
        idItem: currentPlanId,
        type: "count_appointment",
        details: [
          t("plan.log.stageAdded", { count: nextCount }),
          stageTypeName,
        ]
          .filter(Boolean)
          .join("; "),
      });
    } finally {
      setBlocked(false);
    }
  };

  // Удаление приёма (крестик на шапке столбца схемы). Приём может быть
  // любым: бэкенд (action `remove_appointment`) удаляет назначения этого
  // приёма и сдвигает последующие номера на один вниз, чтобы в схеме не
  // оставалось «дырки». Подтверждение — стандартный window.confirm.
  const handleRemoveStage = async (number) => {
    const currentPlanId = getCurrentPlanId();
    const stageNumber = Number(number);
    if (!currentPlanId || blocked) return;
    if (!Number.isFinite(stageNumber) || stageNumber <= 0) return;

    const currentMax = getVisibleStageCount(patient);
    if (stageNumber > currentMax || currentMax <= 1) return;

    const confirmed = window.confirm(
      t("plan.toast.confirmRemoveStage", { number: stageNumber }),
    );
    if (!confirmed) return;

    setBlocked(true);
    try {
      let lastData = await fetchPlan({
        id_plan: currentPlanId,
        action: "remove_appointment",
        appointment_number: stageNumber,
        showFullLoading: false,
        silent: true,
      });

      // ВАЖНО: GetData не выбрасывает ошибку, а возвращает объект ошибки
      // axios (без result). Поэтому «приём удалён» проверяем по НАЛИЧИЮ
      // свежего ответа плана И по уменьшению числа приёмов — иначе сбой
      // запроса выглядел бы как успешное удаление.
      const isPlanPayload = (data) => Boolean(data && data.result);
      const isRemoved = () =>
        isPlanPayload(lastData) && getVisibleStageCount(lastData) < currentMax;

      // Старый бэкенд не знает `remove_appointment` — для ПОСЛЕДНЕГО
      // приёма откатываем то, что делает handleAddStage (уменьшаем лимиты
      // и чистим название этапа). Средние приёмы так удалить нельзя:
      // нумерация сдвинется только на обновлённом бэкенде.
      if (!isRemoved() && stageNumber === currentMax) {
        const planCount =
          Number(patient?.result?.count_appointment?.value) || 0;
        if (planCount >= currentMax) {
          lastData = await fetchPlan({
            id_plan: currentPlanId,
            action: "count_appointment",
            value: "minus",
            showFullLoading: false,
            silent: true,
          });
        }

        const groupIds = (patient?.result?.groups || [])
          .filter(
            (group) =>
              (Number(group?.count_appointment?.value) || 0) >= currentMax,
          )
          .map((group) => Number(group?.id?.value) || 0)
          .filter((id) => id > 0);

        for (const groupId of groupIds) {
          lastData = await fetchPlan({
            id_plan: currentPlanId,
            id_group: groupId,
            action: "count_appointment_group",
            value: "minus",
            showFullLoading: false,
            silent: true,
          });
        }

        lastData = await fetchPlan({
          id_plan: currentPlanId,
          action: "set_appointment_type",
          appointment_number: stageNumber,
          appointment_type_name: "",
          appointment_type_name_en: "",
          showFullLoading: false,
          silent: true,
        });
      }

      if (!isRemoved()) {
        setToast({
          title: isPlanPayload(lastData)
            ? t("plan.toast.stageRemoveNeedsUpdate")
            : t("plan.toast.stageRemoveFailed"),
          status: "error",
        });
        return;
      }

      const nextCount = getVisibleStageCount(lastData || patient);
      await logPlanChange("patient_plan_appointment_count_decreased", {
        idItem: currentPlanId,
        type: "count_appointment",
        details: t("plan.log.stageRemoved", { count: nextCount }),
      });
      setToast({
        title: t("plan.toast.stageRemoved", { number: stageNumber }),
        status: "success",
      });
    } finally {
      setBlocked(false);
    }
  };

  // Смена типа приёма (подписи этапа) кликом по шапке столбца в схеме.
  // Пустое название сбрасывает подпись к «Приём N».
  const handleChangeStageType = async (number, name = "", nameEn = "") => {
    const currentPlanId = getCurrentPlanId();
    const appointmentNumber = Number(number);
    if (!currentPlanId || blocked) return;
    if (!Number.isFinite(appointmentNumber) || appointmentNumber <= 0) return;

    const nextType = String(name || "").trim();
    const nextTypeEn = String(nameEn || "").trim();
    // Сравниваем с сохранёнными названиями (ru/en), а не с локализованной
    // подписью: иначе на en/ar запрос уходил бы даже без изменения типа.
    const currentType = appointmentTypeNamesMap[appointmentNumber] || {
      name: "",
      nameEn: "",
    };
    if (currentType.name === nextType && currentType.nameEn === nextTypeEn) return;

    setBlocked(true);
    try {
      const data = await fetchPlan({
        id_plan: currentPlanId,
        action: "set_appointment_type",
        appointment_number: appointmentNumber,
        appointment_type_name: nextType,
        appointment_type_name_en: nextTypeEn,
        showFullLoading: false,
      });

      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_appointment_type_changed", {
          idItem: currentPlanId,
          type: "appointment_type",
          details: [
            t("plan.log.appointmentTypeNumber", {
              number: appointmentNumber,
            }),
            nextType || t("plan.log.appointmentTypeCleared"),
          ].join(": "),
        });
      }
    } finally {
      setBlocked(false);
    }
  };

  const handlePlanStatusChange = async (nextStatus) => {
    if (blocked) return;
    const currentStatus = patient?.result?.status?.value;
    if (!nextStatus || nextStatus === currentStatus) return;

    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        action: "change_status_plan",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        new_status_plan: nextStatus,
      });

      if (!isSuccessResponse(data)) {
        setToast({
          title: data?.message || t("plan.toast.statusChangeFailed"),
          status: "error",
        });
        return;
      }

      await applyPlanResponse(data);
      setToast({
        title: data?.message || t("plan.toast.statusChanged"),
        status: data?.status || "success",
      });
      await logPlanChange("patient_plan_status_changed", {
        type: "status",
        details: t("plan.log.newStatus", {
          status:
            nextStatus === "active"
              ? t("plan.header.statusActive")
              : t("plan.header.statusArchived"),
        }),
      });
    } catch (err) {
      console.error("Ошибка изменения статуса плана лечения:", err);
      setToast({
        title: t("plan.toast.statusChangeFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  // Карта id курса -> цвет для подсветки строк услуг
  const courseIdToColor = React.useMemo(() => {
    const map = {};
    (patient?.result?.courses || []).forEach((c) => {
      const id = c?.id?.value;
      const color = c?.color?.value;
      if (id && color) map[id] = color;
    });
    return map;
  }, [patient?.result?.courses]);

  // Карта id курса -> название (для заголовка курса в таблице)
  const courseIdToName = React.useMemo(() => {
    const map = {};
    (patient?.result?.courses || []).forEach((c) => {
      const id = c?.id?.value;
      const name = c?.name?.value;
      if (id && name) map[id] = name;
    });
    return map;
  }, [patient?.result?.courses]);

  // Функция для получения максимального номера приема
  const getMaxAppointmentNumber = (groups) => {
    let maxNumber = 0;
    groups.forEach((group) => {
      group.items.forEach((item) => {
        if (item.appointments) {
          item.appointments.forEach((appointment) => {
            const appointmentNumber = parseInt(
              appointment.appointment_number.value,
            );
            if (appointmentNumber > maxNumber) {
              maxNumber = appointmentNumber;
            }
          });
        }
      });
    });
    return maxNumber;
  };

  // Функция для рендеринга статуса приема по номеру
  const renderAppointmentStatus = (appointments, appointmentNumber) => {
    if (appointments) {
      const appointment = appointments.find(
        (apt) => parseInt(apt.appointment_number.value) === appointmentNumber,
      );
      if (appointment) {
        return appointment.status.value === "scheduled" ? "filled" : "empty";
      }
    }
    return "empty";
  };

  const getAppointmentForItem = (item, appointmentNumber) => {
    if (!item?.appointments) return null;
    return item.appointments.find(
      (apt) => parseInt(apt?.appointment_number?.value, 10) === appointmentNumber,
    ) || null;
  };

  const getMarkedAppointmentsCountForItem = (item) => {
    if (!item?.appointments) return 0;

    return item.appointments.filter((apt) => {
      const hasAdmission = apt?.id_admission?.value !== undefined && String(apt.id_admission.value) !== "0";
      const isScheduled = apt?.status?.value === "scheduled";
      return hasAdmission || isScheduled;
    }).length;
  };

  // Проверка: в данной группе приём с номером отмечен — либо есть id_admission (отметили в "Список приёмов"),
  // либо есть запись назначения со status "scheduled" (отметили ячейку в плане)
  const isAppointmentMarkedInGroup = (group, appointmentNumber) => {
    if (!group?.items) return false;
    for (const item of group.items) {
      const apt = (item.appointments || []).find(
        (a) => parseInt(a.appointment_number?.value) === appointmentNumber,
      );
      if (!apt) continue;
      const hasAdmission = apt.id_admission?.value !== undefined && String(apt.id_admission.value) !== "0";
      const isScheduled = apt.status?.value === "scheduled";
      if (hasAdmission || isScheduled) return true;
    }
    return false;
  };

  // Получить информацию о приёме (patient_admission) для группы и номера приёма
  const getAdmissionInfoForGroupAppointment = (group, appointmentNumber) => {
    if (!patient?.result?.admission || !group?.items) return null;

    for (const item of group.items) {
      const apt = (item.appointments || []).find(
        (a) => parseInt(a.appointment_number?.value) === appointmentNumber,
      );
      if (!apt) continue;
      const admissionId = apt.id_admission?.value;
      if (!admissionId || String(admissionId) === "0") continue;

      const info = patient.result.admission[admissionId];
      if (info) return info;
    }

    return null;
  };

  // Форматирование даты в вид dd.mm.YYYY, пустые/нулевые даты отображаем как длинное тире
  const formatDateShort = (dateString) => {
    if (
      !dateString ||
      dateString === "0000-00-00 00:00" ||
      dateString === "0000-00-00 00:00:00"
    ) {
      return "—";
    }

    const [datePart] = dateString.split(" ");
    const [year, month, day] = datePart.split("-");

    if (!year || !month || !day) {
      return "—";
    }

    return `${day}.${month}.${year}`;
  };

  // Отметка этапа в таймлайне: галочки «пройдено» + комментарий.
  // Отправляем только изменения, чтобы не дёргать сервер зря.
  const handleTimelineCompleteStage = async ({
    stage,
    services = [],
    checked = {},
    initialChecked = {},
    comment = "",
  } = {}) => {
    const idPlan = patient?.result?.id?.value;
    if (!idPlan || blocked) return;

    const changed = services.filter(
      (service) =>
        Boolean(checked[service.key]) !== Boolean(initialChecked[service.key]),
    );
    const previousComment = String(stage?.comment || "").trim();
    const commentChanged = comment !== previousComment;

    if (!changed.length && !commentChanged) return;

    setBlocked(true);
    try {
      for (const service of changed) {
        await fetchPlan({
          id_plan: idPlan,
          id_group: service.groupId,
          id_item: service.itemId,
          id_appointment: service.appointmentNumber,
          action: "appointment_done",
          value: checked[service.key] ? "1" : "0",
          showFullLoading: false,
        });
      }

      // Комментарий хранится у приёма каждой услуги этапа — так он читается
      // и в таймлайне, и в таблице/схеме плана.
      if (commentChanged) {
        for (const service of services) {
          await fetchPlan({
            id_plan: idPlan,
            id_group: service.groupId,
            id_item: service.itemId,
            id_appointment: service.appointmentNumber,
            action: "appointment_comment",
            value: comment,
            comment,
            showFullLoading: false,
          });
        }
      }

      setToast({
        title: t("plan.timeline.saved"),
        status: "success",
      });

      await logPlanChange("patient_plan_appointment_checked", {
        type: "timeline_stage",
        details: t("plan.timeline.logDetails", {
          number: stage?.number || "—",
          count: changed.length,
        }),
      });
    } catch (err) {
      console.error("Ошибка отметки этапа:", err);
      setToast({ title: t("plan.timeline.saveFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };

  // Открыть модальное окно «Указать дату приёма»
  const handleOpenSetDateModal = (appointmentNumber, idTreatmentPlanGroup) => {
    setSetDateParams({ appointmentNumber, groupId: idTreatmentPlanGroup });
    setShowSetDateModal(true);
  };

  const handleOpenAppointmentCommentModal = (
    appointmentNumber,
    idTreatmentPlanGroup,
    itemId,
    currentComment = "",
  ) => {
    setAppointmentCommentParams({
      appointmentNumber,
      groupId: idTreatmentPlanGroup,
      itemId,
    });
    setAppointmentCommentValue(currentComment || "");
    setShowAppointmentCommentModal(true);
  };

  const handleOpenAppointmentCommentViewModal = (comment = "") => {
    setAppointmentCommentViewValue(String(comment || "").trim());
    setShowAppointmentCommentViewModal(true);
  };

  // Сохранить плановую дату приёма — создание записи в patient_admission с датой
  const handleSaveAdmissionDate = async (data) => {
    const { appointmentNumber, groupId } = setDateParams;
    if (blocked || !appointmentNumber || !groupId) return;
    const idPlan = patient?.result?.id?.value;
    if (!idPlan) return;
    setBlocked(true);
    try {
      const response = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_treatment_plan: idPlan,
        id_treatment_plan_group: groupId,
        appointment_number: appointmentNumber,
        date: data.date || "",
        action: "set_admission_date",
      });
      await applyPlanResponse(response);
      setShowSetDateModal(false);
      setSetDateParams({ appointmentNumber: null, groupId: null });
      setToast({
        title: response?.message || t("plan.toast.planDateSaved"),
        status: response?.status || "success",
      });
      if (isSuccessResponse(response)) {
        await logPlanChange("patient_plan_admission_date_set", {
          idItem: groupId,
          type: "set_admission_date",
          details: t("plan.log.appointmentDate", {
            number: appointmentNumber,
            date: data?.date || "—",
          }),
        });
      }
    } catch (err) {
      console.error("Ошибка сохранения даты приёма:", err);
      setToast({ title: t("plan.toast.planDateSaveFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };

  const handleSaveAppointmentComment = async (data) => {
    const { appointmentNumber, groupId, itemId } = appointmentCommentParams;
    if (blocked || !appointmentNumber || !groupId || !itemId) return;
    const idPlan = patient?.result?.id?.value;
    if (!idPlan) return;
    const commentValue = String(data?.comment ?? "").trim();
    setBlocked(true);
    try {
      const response = await fetchPlan({
        id_plan: idPlan,
        id_group: groupId,
        id_item: itemId,
        id_appointment: appointmentNumber,
        action: "appointment_comment",
        value: commentValue,
        comment: commentValue,
        showFullLoading: false,
      });
      if (isSuccessResponse(response)) {
        setShowAppointmentCommentModal(false);
        setAppointmentCommentParams({ appointmentNumber: null, groupId: null, itemId: null });
        setAppointmentCommentValue("");
      }
    } finally {
      setBlocked(false);
    }
  };

  // Функция для расчета скидки
  const calculateDiscount = (amountBase, amountTotal) => {
    const base = parseFloat(amountBase);
    const total = parseFloat(amountTotal);
    if (base > total) {
      const discount = (((base - total) / base) * 100).toFixed(0);
      return {
        hasDiscount: true,
        discountPercent: discount,
        discountAmount: base - total,
      };
    }
    return { hasDiscount: false };
  };

  useEffect(() => {
    if (!showModalServices) return;
    if (!selectedGroupType) return;

    const query = (serviceSearchQuery || "").trim();

    // не дергаем бек, пока не введут минимум 2 символа
    if (query.length < 2) {
      setServiceSearchResults([]);
      setServiceSearchLoading(false);
      setServiceSearchError(null);
      return;
    }

    setServiceSearchLoading(true);
    setServiceSearchError(null);

    const handle = setTimeout(async () => {
      try {
        const response = await GetData({
          module: "patient",
          block: "treatment_plan",
          id_patient: patientId,
          id_plan: patient?.result?.id?.value || 0,
          id_group: selectedGroupId || 0,
          action: "get_available_services",
          group_type: selectedGroupType,
          clinic_id: serviceClinicId ? Number(serviceClinicId) : undefined,
          q: query,
          limit: 50,
        });

        const services = response?.services || [];
        setServiceSearchResults(services);
      } catch (err) {
        console.error("Ошибка поиска услуг:", err);
        setServiceSearchError(err?.message || "search_error");
        setServiceSearchResults([]);
      } finally {
        setServiceSearchLoading(false);
      }
    }, 250);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showModalServices,
    selectedGroupType,
    selectedGroupId,
    serviceSearchQuery,
    serviceClinicId,
    patientId,
  ]);

  // Филиалы для фильтра услуг (один раз на вкладку плана лечения).
  useEffect(() => {
    let cancelled = false;

    const loadServiceFilials = async () => {
      try {
        const response = await GetData({
          module: "patient",
          block: "treatment_plan",
          action: "get_all_clinics",
        });
        if (cancelled) return;
        const filials = Array.isArray(response?.all_clinics)
          ? response.all_clinics
          : [];
        const myClinics = Array.isArray(response?.test?.my_clinics)
          ? response.test.my_clinics.map((id) => String(id))
          : [];
        setServiceFilials(filials);
        setServiceMyClinics(myClinics);
      } catch (err) {
        console.error("Ошибка загрузки филиалов для фильтра услуг:", err);
      }
    };

    loadServiceFilials();
    return () => {
      cancelled = true;
    };
  }, []);

  // Филиал, к которому привязан пользователь (таблица user_clinic). Если
  // привязок несколько — берём первый, который есть в списке филиалов.
  const userClinicId = useMemo(() => {
    const ids = serviceFilials.map((c) => String(c.id));
    return serviceMyClinics.find((id) => ids.includes(id)) || "";
  }, [serviceFilials, serviceMyClinics]);

  // Филиал по умолчанию: филиал пользователя (user_clinic) -> сохранённый -> все.
  useEffect(() => {
    if (!showModalServices) return;
    const ids = serviceFilials.map((c) => String(c.id));
    if (!ids.length) return;

    if (userClinicId && ids.includes(userClinicId)) {
      setServiceClinicId(userClinicId);
      return;
    }
    const saved = String(get("filters.plan.clinic_id", "") || "");
    setServiceClinicId(saved && ids.includes(saved) ? saved : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModalServices, serviceFilials, serviceMyClinics, userClinicId]);

  useEffect(() => {
    setPlanName(patient?.result?.name?.value || "");
  }, [patient?.result?.name?.value]);

  useEffect(() => {
    const currentDate =
      patient?.result?.treatment_start_date?.value ||
      patient?.result?.date_start_treatment?.value ||
      patient?.result?.date_start?.value ||
      patient?.result?.start_date?.value ||
      "";
    setTreatmentStartDate(currentDate ? currentDate.split(" ")[0] : "");
  }, [
    patient?.result?.treatment_start_date?.value,
    patient?.result?.date_start_treatment?.value,
    patient?.result?.date_start?.value,
    patient?.result?.start_date?.value,
  ]);

  if (loading) return <div className="loading">{t("state.loading", { ns: "common" })}</div>;
  if (error) return <div className="error">{t("error", { ns: "common", message: error })}</div>;
  if (!patient) return <div className="no-data">{t("plan.dataNotFound")}</div>;

  // Смена филиала в фильтре услуг: применяем и запоминаем в настройках.
  const handleServiceClinicChange = (value) => {
    setServiceClinicId(value);
    update({ filters: { plan: { clinic_id: value } } });
  };

  // Открытие модального окна и получение услуг текущей группы
  const handleModalOpenServices = async (groupType, groupId) => {
    setSelectedGroupType(groupType);
    setSelectedGroupId(groupId);
    setServiceSelectMode("add_service");
    setSelectedParentItemId(null);
    setPendingStageNumber(null);
    setServiceSearchQuery("");
    setServiceSearchResults([]);
    setServiceSearchError(null);
    setShowModalServices(true);
  };

  // Добавление услуги из окна приёма схемы лечения: выбранная услуга
  // сразу привязывается к этому приёму (номер этапа уходит на бэкенд).
  const handleOpenAddServiceFromStage = (group, appointmentNumber) => {
    // `group.id`/`group.type` могут прийти как {value, access} или как скаляр —
    // поддерживаем оба варианта, иначе группа теряется и добавление не работает.
    const groupId = group?.id?.value ?? group?.id ?? null;
    const groupType = group?.type?.value ?? group?.type ?? null;
    if (!groupId || !appointmentNumber) {
      setToast({ title: t("plan.toast.groupNotDetermined"), status: "error" });
      return;
    }
    handleModalOpenServices(groupType, groupId);
    setPendingStageNumber(appointmentNumber);
  };

  // Открытие модального окна выбора подуслуги для конкретной услуги
  const handleModalOpenSubitemServices = async (groupType, parentItemId) => {
    setSelectedGroupType(groupType);
    setSelectedGroupId(null);
    setServiceSelectMode("add_subitem");
    setSelectedParentItemId(parentItemId);
    setServiceSearchQuery("");
    setServiceSearchResults([]);
    setServiceSearchError(null);
    setShowModalServices(true);
  };

  //Добавление новой услуги
  const handleAddService = async (selectedService) => {
    if (!selectedService) return;

    // Молча выходить нельзя: пользователь видит, что клик по услуге
    // «ничего не делает». Поэтому любая причина отказа показывается тостом.
    if (blocked) {
      setToast({ title: t("plan.toast.actionBlocked"), status: "error" });
      return;
    }
    if (!selectedGroupId) {
      setToast({ title: t("plan.toast.groupNotDetermined"), status: "error" });
      return;
    }

    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        id_group: Number(selectedGroupId),
        action: "add_service",
        service_id: selectedService.id,
        // group_type бэкенду для добавления не нужен (он только для поиска),
        // поэтому отправляем его только если он известен.
        ...(selectedGroupType ? { group_type: selectedGroupType } : {}),
        appointment_number: Number(pendingStageNumber) || undefined,
      });

      console.log("[add_service] запрос/ответ", {
        request: {
          id_plan: patient?.result?.id?.value || 0,
          id_group: Number(selectedGroupId),
          service_id: selectedService.id,
          group_type: selectedGroupType || null,
          appointment_number: Number(pendingStageNumber) || undefined,
        },
        status: data?.status,
        message: data?.message,
        test: data?.test,
      });

      // Без результата плана закрывать окно нельзя: услуга не добавлена,
      // а пользователь об этом не узнает (бэкенд старой версии/ошибка сети).
      if (!isSuccessResponse(data) || !data?.result) {
        console.warn("add_service: неожиданный ответ бэкенда", data);
        setToast({
          title: data?.message || t("plan.toast.serviceAddFailed"),
          status: "error",
        });
        return;
      }

      // Реальная проверка результата: услуга должна появиться в направлении.
      // Старый код закрывал окно с тостом «Услуга успешно добавлена» даже когда
      // бэкенд ничего не сделал (status пустой), и выглядело как «не добавляется».
      const sameGroup = (group) =>
        String(group?.id?.value) === String(selectedGroupId);
      const itemsCount = (plan) =>
        (plan?.result?.groups || []).find(sameGroup)?.items?.length ?? 0;
      const countBefore = itemsCount(patient);
      const countAfter = itemsCount(data);
      const cellCount = (plan, number) => {
        const group = (plan?.result?.groups || []).find(sameGroup);
        return (group?.items || []).filter((item) =>
          (item?.appointments || []).some(
            (apt) => Number(apt?.appointment_number?.value) === number,
          ),
        ).length;
      };

      if (countAfter <= countBefore) {
        console.warn(
          "add_service: услуга не появилась в плане",
          { countBefore, countAfter, status: data?.status, test: data?.test },
        );
        setToast({
          title: t("plan.toast.serviceAddNotApplied"),
          status: "error",
        });
        return;
      }

      // Услуга могла добавиться в направление, но не привязаться к приёму
      // (старый бэкенд не понял appointment_number) — тогда в схеме она
      // невидима, хотя в таблице есть. Досылаем привязку и КАЖДЫЙ раз
      // проверяем результат по свежим данным плана.
      const stageNumber = Number(pendingStageNumber) || 0;
      // Снимки «до» запоминаем сразу: дальше patient перезаписывается
      // перечитанным планом, и сравнивать с ним уже нельзя.
      const cellBefore = cellCount(patient, stageNumber);
      const groupItems = (plan) =>
        (plan?.result?.groups || []).find(sameGroup)?.items || [];
      const idsBefore = new Set(
        groupItems(patient).map((item) => String(item?.id?.value)),
      );

      // Новая услуга — та, которой НЕ БЫЛО в направлении до добавления.
      // Если бэкенд не отдал id_service (старые записи), сверка по id_service
      // не сработает, а по разнице id — сработает всегда.
      const pickMax = (list) =>
        list.reduce(
          (best, item) =>
            Number(item?.id?.value) > Number(best?.id?.value ?? 0) ? item : best,
          null,
        );
      const findNewItem = (plan) => {
        const items = groupItems(plan);
        const fresh = items.filter(
          (item) => !idsBefore.has(String(item?.id?.value)),
        );
        const freshMax = pickMax(fresh);
        if (freshMax) return freshMax;
        return pickMax(
          items.filter(
            (item) =>
              String(item?.id_service?.value) === String(selectedService.id),
          ),
        );
      };
      const itemHasStage = (item) =>
        (item?.appointments || []).some(
          (apt) => Number(apt?.appointment_number?.value) === stageNumber,
        );

      let linked = stageNumber > 0 && cellCount(data, stageNumber) > cellBefore;
      let latestPlan = data;

      // Перечитать план с сервера и пересчитать признак привязки.
      const recheck = async () => {
        const refreshed = await refreshCurrentPlan();
        if (!refreshed) return false;
        latestPlan = refreshed;
        return cellCount(refreshed, stageNumber) > cellBefore;
      };

      if (stageNumber > 0 && !linked) {
        let newItemId = Number(findNewItem(data)?.id?.value) || 0;
        // Если не нашли в ответе — смотрим на свежий план с сервера.
        if (!newItemId) {
          const refreshed = await refreshCurrentPlan();
          if (refreshed) {
            latestPlan = refreshed;
            linked = cellCount(refreshed, stageNumber) > cellBefore;
            newItemId = Number(findNewItem(refreshed)?.id?.value) || 0;
          }
        }
        if (!newItemId) {
          newItemId = Number(data?.test?.add_service_debug?.new_item_id) || 0;
        }

        console.log("[add_service link] параметры привязки", {
          newItemId,
          stageNumber,
          idPlan: patient?.result?.id?.value || 0,
          idGroup: Number(selectedGroupId),
          serviceId: selectedService.id,
          // 0 = сервер получил appointment_number, но запись приёма НЕ создал
          serverLinkId: Number(
            data?.test?.add_service_debug?.appointment_link_id,
          ),
        });

        if (!newItemId) {
          console.warn(
            "add_service: не удалось определить id добавленной услуги",
            { test: data?.test, groupItems: groupItems(latestPlan).length },
          );
        } else {
          // 1) Новый бэкенд: явная привязка по id услуги и номеру приёма.
          try {
            const linkResponse = await GetData({
              module: "patient",
              block: "treatment_plan",
              id_patient: patientId,
              id_plan: patient?.result?.id?.value || 0,
              id_group: Number(selectedGroupId),
              action: "link_item_appointment",
              id_item: newItemId,
              appointment_number: stageNumber,
            });
            console.log("[link_item_appointment]", {
              id_item: newItemId,
              appointment_number: stageNumber,
              status: linkResponse?.status,
              message: linkResponse?.message,
            });
            linked = await recheck();
          } catch (linkErr) {
            console.error("Не удалось привязать услугу к приёму:", linkErr);
          }

          // 2) Старый бэкенд: действие `appointment` (то же, что у чекбоксов
          //    в таблице плана) создаёт запись «услуга + приём». Оно
          //    ПЕРЕКЛЮЧАЕТ отметку, поэтому вызываем только если у услуги
          //    ещё нет этого приёма в свежих данных.
          if (!linked) {
            const currentItem =
              findNewItem(latestPlan) || findNewItem(data) || null;
            if (!currentItem || !itemHasStage(currentItem)) {
              try {
                const toggleResponse = await GetData({
                  module: "patient",
                  block: "treatment_plan",
                  id_patient: patientId,
                  id_plan: patient?.result?.id?.value || 0,
                  id_group: Number(selectedGroupId),
                  id_item: newItemId,
                  id_appointment: stageNumber,
                  appointment_number: stageNumber,
                  value: "check",
                  action: "appointment",
                  showFullLoading: false,
                });
                console.log("[appointment toggle fallback]", {
                  id_item: newItemId,
                  id_appointment: stageNumber,
                  status: toggleResponse?.status,
                  message: toggleResponse?.message,
                });
                linked = await recheck();
              } catch (toggleErr) {
                console.error("Резервная привязка (appointment) не удалась:", toggleErr);
              }
            }
          }
        }
      }

      // Состояние плана применяем ВСЕГДА, в том числе когда услуга добавлена
      // в конкретный приём (stageNumber > 0) и привязка удалась. Раньше в этом
      // случае setPatient не вызывался, и схема лечения перерисовывалась
      // только после перезагрузки страницы.
      const applied = await applyPlanResponse(latestPlan || data);
      if (!applied) await refreshCurrentPlan();
      setShowModalServices(false);

      if (stageNumber > 0 && !linked) {
        console.warn("add_service: услуга добавлена без привязки к приёму", {
          stageNumber,
          idPlan: patient?.result?.id?.value || 0,
          idGroup: Number(selectedGroupId),
          serviceId: selectedService.id,
          serverStatus: data?.status,
          serverMessage: data?.message,
        });
        setToast({
          title: t("plan.toast.serviceAddedNotLinked"),
          status: "error",
        });
      } else {
        setToast({
          title: data?.message || t("plan.toast.serviceAdded"),
          status: data?.status || "success",
        });
      }

      setPendingStageNumber(null);
      setSelectedServiceId("");
      setServiceSearchQuery("");
      setServiceSearchResults([]);
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_service_added", {
          idItem: selectedService.id,
          type: "add_service",
          details: selectedService.name || t("plan.log.serviceId", { id: selectedService.id }),
        });
      }
    } catch (err) {
      console.error("Ошибка добавления услуги:", err);
      setToast({ title: t("plan.toast.serviceAddFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };

  // Открытие модального окна добавления своей (не из каталога) услуги.
  // `appointmentNumber` передаётся при добавлении из окна приёма схемы:
  // созданная услуга сразу привязывается к этому приёму.
  const handleOpenModalCustomService = (
    groupType,
    groupId,
    appointmentNumber = null,
  ) => {
    setCustomServiceContext({ groupType, groupId, appointmentNumber });
    setCustomServiceName("");
    setCustomServicePrice("");
    setShowModalCustomService(true);
  };

  // «Добавить новую» из окна приёма схемы лечения: сохраняем номер приёма,
  // чтобы услуга попала именно в него, а не просто в направление.
  const handleOpenAddCustomServiceFromStage = (group, appointmentNumber) => {
    const groupId = group?.id?.value ?? group?.id ?? null;
    const groupType = group?.type?.value ?? group?.type ?? null;
    if (!groupId || !appointmentNumber) {
      setToast({ title: t("plan.toast.groupNotDetermined"), status: "error" });
      return;
    }
    handleOpenModalCustomService(groupType, groupId, appointmentNumber);
  };

  //Добавление новой услуги только в план лечения (без каталога)
  const handleSubmitCustomService = async () => {
    if (blocked) return;
    const serviceName = customServiceName.trim();
    const groupId = Number(customServiceContext.groupId) || 0;
    if (!serviceName || !groupId) return;

    // Номер приёма: при добавлении из окна приёма схемы лечения услуга
    // должна попасть именно в этот приём, а не просто в направление.
    const stageNumber = Number(customServiceContext.appointmentNumber) || 0;
    // Цена вводится в том же окне: нормализуем запятую в точку.
    const price = String(customServicePrice || "")
      .replace(/\s/g, "")
      .replace(",", ".");

    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        id_group: groupId,
        action: "add_service_custom",
        name: serviceName,
        amount_base: price,
        appointment_number: stageNumber || undefined,
      });

      // Бэкенд подтверждает успех только статусом success. Пустой статус
      // означает, что действие не выполнено (например, бэкенд старой версии).
      if (!data || data.status !== "success") {
        console.warn("add_service_custom: неожиданный ответ бэкенда", data);
        setToast({
          title: data?.message || t("plan.toast.serviceAddFailed"),
          status: "error",
        });
        return;
      }

      let latestPlan = await applyPlanResponse(data);

      // Проверяем, что услуга попала в ячейку приёма. Если бэкенд не понял
      // appointment_number, досылаем привязку отдельным действием — иначе
      // услуга есть в направлении, но невидима в схеме лечения.
      let linked = true;
      if (stageNumber > 0) {
        const stageItems = (plan) => {
          const planGroup = (plan?.result?.groups || []).find(
            (item) => String(item?.id?.value) === String(groupId),
          );
          return (planGroup?.items || []).filter((item) =>
            (item?.appointments || []).some(
              (apt) => Number(apt?.appointment_number?.value) === stageNumber,
            ),
          );
        };
        linked = stageItems(latestPlan).length > 0;

        if (!linked) {
          const findAddedItem = (plan) => {
            const planGroup = (plan?.result?.groups || []).find(
              (item) => String(item?.id?.value) === String(groupId),
            );
            const matches = (planGroup?.items || []).filter(
              (item) => String(item?.name?.value ?? "").trim() === serviceName,
            );
            return matches.reduce(
              (best, item) =>
                Number(item?.id?.value) > Number(best?.id?.value ?? 0)
                  ? item
                  : best,
              null,
            );
          };
          const newItemId = Number(findAddedItem(latestPlan)?.id?.value) || 0;
          if (newItemId) {
            try {
              await GetData({
                module: "patient",
                block: "treatment_plan",
                id_patient: patientId,
                id_plan: patient?.result?.id?.value || 0,
                id_group: groupId,
                action: "link_item_appointment",
                id_item: newItemId,
                appointment_number: stageNumber,
              });
            } catch (linkErr) {
              console.error(
                "Не удалось привязать новую услугу к приёму:",
                linkErr,
              );
            }
            const refreshed = await refreshCurrentPlan();
            if (refreshed) {
              latestPlan = refreshed;
              linked = stageItems(refreshed).length > 0;
            }
          }
        }

        if (!linked) {
          console.warn(
            "add_service_custom: услуга добавлена без привязки к приёму",
            { stageNumber, idGroup: groupId, name: serviceName },
          );
        }
      }

      setShowModalCustomService(false);
      setCustomServiceName("");
      setCustomServicePrice("");
      setCustomServiceContext({
        groupType: null,
        groupId: null,
        appointmentNumber: null,
      });
      setToast({
        title: linked
          ? data?.message || t("plan.toast.serviceAdded")
          : t("plan.toast.serviceAddedNotLinked"),
        status: linked ? data?.status || "success" : "error",
      });
      await logPlanChange("patient_plan_service_added", {
        type: "add_service_custom",
        details: serviceName,
      });
    } catch (err) {
      console.error("Ошибка добавления новой услуги:", err);
      setToast({ title: t("plan.toast.serviceAddFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };

  //Добавление подуслуги к услуге
  const handleAddSubitem = async (selectedService) => {
    if (!selectedService || !selectedParentItemId || blocked) return;

    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        id_item: selectedParentItemId,
        action: "add_subitem",
        service_id: selectedService.id,
      });

      await applyPlanResponse(data);
      setShowModalServices(false);
      setSelectedServiceId("");
      setSelectedParentItemId(null);
      setServiceSelectMode("add_service");
      setServiceSearchQuery("");
      setServiceSearchResults([]);
      setToast({
        title: data?.message || t("plan.toast.subitemAdded"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_subitem_added", {
          idItem: selectedService.id,
          type: "add_subitem",
          details: t("plan.log.subitemToService", {
            sub: selectedService.name || selectedService.id,
            parent:
              getServiceNameByItemId(selectedParentItemId) ||
              selectedParentItemId,
          }),
        });
      }
    } catch (err) {
      console.error("Ошибка добавления подуслуги:", err);
      setToast({ title: t("plan.toast.subitemAddFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };
  // Загрузка доступных направлений (для схемы — без открытия модалки:
  // список показывается в раскрывающемся окне кнопки «+ Добавить группу»).
  const loadAvailableGroups = async () => {
    const currentPlanId = getCurrentPlanId();
    if (!currentPlanId) {
      setToast({
        title: t("plan.toast.planNotDetermined"),
        status: "error",
      });
      return [];
    }

    setLoadingGroup(true);
    try {
      const response = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: currentPlanId,
        action: "get_available_group",
      });
      const groups = response.groups || [];
      return groups;
    } catch (err) {
      console.error("Ошибка загрузки группы услуг:", err);
      setToast({
        title: t("plan.toast.groupLoadFailed"),
        status: "error",
      });
      return [];
    } finally {
      setLoadingGroup(false);
    }
  };

  // Выбор направления (режим «Группа», т.е. таблица) — раскрывающееся меню
  // самой кнопки «Добавить группу», без отдельного попапа.
  const handleAddGroup = async (selectedGroup) => {
    if (!selectedGroup || blocked) return;
    const currentPlanId = getCurrentPlanId();
    if (!currentPlanId) {
      setToast({
        title: t("plan.toast.planNotDetermined"),
        status: "error",
      });
      return;
    }

    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        id_patient: patientId,
        id_plan: currentPlanId,
        action: "add_group",
        id_group: selectedGroup.id,
      });

      await applyPlanResponse(data);
      setToast({
        title: data?.message || t("plan.toast.groupAdded"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_group_added", {
          idItem: selectedGroup.id,
          type: "add_group",
          details: selectedGroup.name || t("plan.log.groupId", { id: selectedGroup.id }),
        });
      }
    } catch (err) {
      console.error("Ошибка добавления группы:", err);
      setToast({ title: t("plan.toast.groupAddFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };

  // получение параметров услуги
  const handleModalChangeServices = async (service_id) => {
    setLoadingService(true);
    try {
      const response = await GetData({
        module: "patient",
        block: "treatment_plan",
        service_id: service_id,
        action: "get_parametrs_service",
      });

      setParamsService(response.result);
      setShowService(true);
    } catch (err) {
      console.error("Ошибка загрузки формы изменения услуги:", err);
      setToast({
        title: t("plan.toast.editFormLoadFailed"),
        status: "error",
      });
      setShowService(false);
    } finally {
      setLoadingService(false);
    }
  };

  // добавление изменений параметров услуги
  const handleSaveParamsService = async (updatedData) => {
    try {
      setBlocked(true);

      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        action: "add_changes_service",
        service_id: paramsService.id,
        amount_base: updatedData.amount_base,
        name: updatedData.name,
        composition: updatedData.composition,
        technology: updatedData.technology,
        sort: updatedData.sort,
      });

      await applyPlanResponse(data);
      setShowService(false);
      setToast({
        title: data?.message || t("plan.toast.serviceChanged"),
        status: data?.status || "success",
      });
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_service_updated", {
          idItem: paramsService.id,
          type: "add_changes_service",
          details: updatedData?.name || getServiceNameByItemId(paramsService.id) || t("plan.log.serviceId", { id: paramsService.id }),
        });
      }
    } catch (err) {
      console.error("Ошибка изменения услуги:", err);
      setToast({ title: t("plan.toast.serviceChangeFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };

  // Открытие модального окна и получение клиник
  const handleModalOpenPay = async (id_treatment_plan) => {
    setBlocked(true);
    try {
      const [clinicsResponse, staffResponse] = await Promise.all([
        GetData({
          module: "patient",
          block: "treatment_plan",
          action: "get_all_clinics",
        }),
        GetData({
          module: "staff",
          block: "list",
        }),
      ]);

      const rawStaff = staffResponse?.result || [];
      setPayModalStaff(
        rawStaff.map((s) => ({
          id: s.id?.value ?? s.id,
          name: s.name?.value ?? s.name ?? "",
        }))
      );

      setPaymentGroupId(id_treatment_plan);
      setAvailableClinics(clinicsResponse.all_clinics || []);
      setShowModalPay(true);
    } catch (err) {
      console.error("Ошибка загрузки клиник:", err);
      setToast({ title: t("plan.toast.clinicsLoadFailed"), status: "error" });
    } finally {
      // ВАЖНО: снимать нужно именно blocked — иначе после открытия окна
      // оплаты флаг остаётся true до перезагрузки страницы и ВСЕ действия
      // плана (в т.ч. добавление услуги) молча перестают работать.
      setBlocked(false);
    }
  };
  //Добавление оплаты
  const handleSavePay = async (paymentData) => {
    setBlocked(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        action: "create_transaction",
        id_patient: paymentData.id_patient,
        id_clinic: paymentData.id_clinic,
        id_group: paymentData.id_treatment_plan,
        payment_method: paymentData.payment_method,
        status: paymentData.status,
        price: paymentData.price,
        ...(paymentData.id_user_owner
          ? { id_user_owner: paymentData.id_user_owner }
          : {}),
      });

      setToast({
        title: data?.message || t("plan.toast.paymentAdded"),
        status: data?.status || "success",
      });
      setShowModalPay(false);
      if (isSuccessResponse(data)) {
        await logPlanChange("patient_plan_payment_added", {
          idItem: paymentData.id_treatment_plan,
          idClinic: paymentData.id_clinic,
          type: "create_transaction",
          details: t("plan.log.paymentAmount", { amount: paymentData.price }),
        });
        const parsedRoutePlanId = Number(routePlanId);
        const currentPlanId =
          Number.isFinite(parsedRoutePlanId) && parsedRoutePlanId > 0
            ? parsedRoutePlanId
            : patient?.result?.id?.value || 0;
        try {
          const refreshedPlan = await GetData({
            module: "patient",
            block: "treatment_plan",
            id_patient: patientId,
            id_plan: currentPlanId,
          });
          if (isSuccessResponse(refreshedPlan)) {
            setPatient(refreshedPlan);
          } else {
            await applyPlanResponse(data);
          }
        } catch (refreshErr) {
          console.error("Ошибка перезагрузки текущего плана после оплаты:", refreshErr);
          await applyPlanResponse(data);
        }
      } else {
        await applyPlanResponse(data);
      }
    } catch (err) {
      console.error("Ошибка добавления оплаты:", err);
      setToast({ title: t("plan.toast.paymentAddFailed"), status: "error" });
    } finally {
      setBlocked(false);
    }
  };
  // const handleGoToPrint = () => {
  //   const currentPlanId = getCurrentPlanId();
  //   if (!currentPlanId) return;
  //   navigate(`/patient/${patientId}/plan/${currentPlanId}/print`);
  // };

  const handleGoToPrintOtherFormat = () => {
    const currentPlanId = getCurrentPlanId();
    if (!currentPlanId) return;
    navigate(`/patient/${patientId}/plan/${currentPlanId}/print-other`);
  };

  // Переходы между экранами раздела («План лечения / Рекомендации /
  // Лист назначений») делает общий компонент PlanTabs.
  const handlePlanNameSave = async () => {
    const currentName = patient?.result?.name?.value || "";
    const nextName = planName.trim();

    if (!nextName || nextName === currentName) return;

    try {
      const data = await GetData({
      module: "patient",
      block: "treatment_plan",
      action: "rename_plan",
      id_patient: patientId,
      id_plan: patient?.result?.id?.value || 0,
      new_name_plan: nextName,
      });

      if (isSuccessResponse(data)) {
        await applyPlanResponse(data);
        setToast({
          title: data?.message || t("plan.toast.planNameUpdated"),
          status: data?.status || "success",
        });
      } else {
        setToast({
          title: data?.message || t("plan.toast.planNameUpdateFailed"),
          status: "error",
        });
        setPlanName(currentName);
      }
    } catch (err) {
      console.error("Ошибка обновления названия плана:", err);
      setToast({
        title: t("plan.toast.planNameUpdateFailed"),
        status: "error",
      });
      setPlanName(currentName);
    }
  };

  const handleTreatmentStartDateSave = async () => {
    const currentDateRaw = patient?.result?.date_start?.value || "";
    const currentDate = currentDateRaw ? currentDateRaw.split(" ")[0] : "";
    const nextDate = treatmentStartDate;

    if (!nextDate || nextDate === currentDate) return;

    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan",
        action: "set_treatment_start_date",
        id_patient: patientId,
        id_plan: patient?.result?.id?.value || 0,
        treatment_start_date: nextDate,
      });

      if (isSuccessResponse(data)) {
        await applyPlanResponse(data);
        setToast({
          title: data?.message || t("plan.toast.startDateUpdated"),
          status: data?.status || "success",
        });
      } else {
        setToast({
          title: data?.message || t("plan.toast.startDateUpdateFailed"),
          status: "error",
        });
        setTreatmentStartDate(currentDate);
      }
    } catch (err) {
      console.error("Ошибка обновления даты начала лечения:", err);
      setToast({
        title: t("plan.toast.startDateUpdateFailed"),
        status: "error",
      });
      setTreatmentStartDate(currentDate);
    }
  };

  const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return "—";
    const [datePart] = String(isoDate).trim().split(/[ T]/);
    const [year, month, day] = datePart.split("-");
    if (!year || !month || !day) return "—";
    return `${day}.${month}.${year}`;
  };

  console.log("PATIENT:", patient);

  // Подписи кнопок. Иконки подставляет BlockTitle по тексту подписи
  // (см. ICON_RULES в components/layout/blockTitle/BlockTitle.js),
  // поэтому inline-SVG здесь больше не нужны.
  const saveIcon = t("plan.buttons.saveChanges");
  const closeIcon = t("plan.buttons.close");
  const editIcon = t("plan.buttons.edit");
  const deletePlanIcon = t("plan.buttons.deletePlan");

  const printButtons = [
    {
      // «Печать» — кнопка только с иконкой (подпись в title/aria-label).
      label: null,
      title: t("plan.buttons.print"),
      icon: "print",
      iconOnly: true,
      onClick: handleGoToPrintOtherFormat,
      variant: "secondary",
    },
  ];

  const planActionButtons = coursesMode
    ? [
        ...printButtons,
        {
          label: closeIcon,
          icon: "close",
          onClick: handleCoursesClose,
          variant: "secondary",
        },
      ]
    : changed
    ? [
        {
          label: saveIcon,
          icon: "save",
          onClick: handleChangedClose,
          variant: "success",
        },
      ]
    : [
        // В схеме и таймлайне курсы не работают: колонка выбора услуг есть
        // только в табличном виде, поэтому кнопку «Курсы» там не показываем.
        ...(viewMode !== "table"
          ? []
          : [
              {
                label: t("plan.buttons.courses"),
                onClick: handleCoursesOpen,
                variant: "secondary",
              },
            ]),
        ...printButtons,
        // В схеме (матрице) и таймлайне режима редактирования нет: кнопку
        // «Редактировать» показываем только в табличном виде плана.
        ...(viewMode !== "table"
          ? []
          : [
              {
                label: editIcon,
                icon: "edit",
                onClick: handleChangedOpen,
                variant: "secondary",
              },
            ]),
      ];

  // «Удалить план» больше не живёт в верхнем ряду кнопок: она рендерится
  // в подвале плана (справа), рядом с «Добавить группу» (слева).
  // В режиме курсов и в режиме редактирования кнопка не нужна.
  const deletePlanHandler = !coursesMode && !changed ? handleDeletePlan : null;

  // Переключатель видов плана — отдельный узел: в шапке он стоит
  // по центру (между заголовком слева и кнопками справа).
  // В режиме редактирования работаем только с таблицей-группой,
  // поэтому выбор вида скрываем целиком.
  const planViewSwitch = changed ? null : (
    <SegmentedControl
      ariaLabel={t("plan.matrix.viewTable")}
      items={[
        { value: "table", label: t("plan.matrix.viewTableLabel") },
        { value: "matrix", label: t("plan.matrix.viewMatrix") },
        { value: "timeline", label: t("plan.matrix.viewTimeline") },
      ]}
      joined
      onChange={changeViewMode}
      size="md"
      value={activeViewMode}
    />
  );

  // Кнопки действий плана — справа в шапке, отдельной группой.
  const planActionsNode = (
    <div className="plan-header-actions">
      {planActionButtons.map((button, index) => (
        <Button
          key={index}
          icon={button.icon}
          iconOnly={button.iconOnly}
          onClick={button.onClick}
          title={button.title}
          variant={button.variant}>
          {button.label}
        </Button>
      ))}
    </div>
  );

  // Метрики плана (режим просмотра): отдельные карточки-плитки с иконками —
  // как .pov-stat в обзоре пациента. Рендерятся ВНЕ карточки плана.
  const planStatusValue = patient?.result?.status?.value || "active";
  // Приёмы идут общими столбцами на все группы, поэтому показываем
  // максимальное число приёмов среди групп, а не сумму (сумма завышала итог).
  const planAppointmentsTotal = (patient?.result?.groups || []).reduce(
    (max, group) => Math.max(max, Number(getGroupAppointmentLimit(group)) || 0),
    0,
  );
  const planMetrics = (
    <div className="plan-metrics pov-metrics">
      <div className="pov-stat">
        <span className="pov-stat__icon" aria-hidden="true">
          <IoCashOutline />
        </span>
        <span className="pov-stat__label">{t("plan.header.totalCard")}</span>
        <span className="pov-stat__value">
          {planTotalToDisplay.toLocaleString(locale)} ₽
        </span>
      </div>
      <div className="pov-stat">
        <span className="pov-stat__icon" aria-hidden="true">
          {planStatusValue === "active" ? (
            <IoCheckmarkCircleOutline />
          ) : (
            <IoAlertCircleOutline />
          )}
        </span>
        <span className="pov-stat__label">{t("plan.header.statusCard")}</span>
        <span
          className={`pov-stat__value plan-stat__value--status is-${planStatusValue}`}>
          {planStatusValue === "active"
            ? t("plan.header.statusActive")
            : t("plan.header.statusArchived")}
        </span>
      </div>
      <div className="pov-stat">
        <span className="pov-stat__icon" aria-hidden="true">
          <IoTimeOutline />
        </span>
        <span className="pov-stat__label">
          {t("plan.header.appointmentsTotal")}
        </span>
        <span className="pov-stat__value">{planAppointmentsTotal}</span>
      </div>
      <div className="pov-stat">
        <span className="pov-stat__icon" aria-hidden="true">
          <IoCalendarOutline />
        </span>
        <span className="pov-stat__label">{t("plan.header.createdCard")}</span>
        <span className="pov-stat__value plan-stat__value--date">
          {formatDateForDisplay(patient?.result?.date_created?.value) || "—"}
        </span>
      </div>
      <div className="pov-stat">
        <span className="pov-stat__icon" aria-hidden="true">
          <IoCalendarClearOutline />
        </span>
        <span className="pov-stat__label">{t("plan.header.startCard")}</span>
        <span className="pov-stat__value plan-stat__value--date">
          {formatDateForDisplay(patient?.result?.date_start?.value) || "—"}
        </span>
      </div>
      {allergyAnamnesisLine || patient?.allergics?.value ? (
        <div className="pov-stat plan-stat--wide">
          <span className="pov-stat__label">{t("plan.header.allergicsCard")}</span>
          <span className="pov-stat__value plan-stat__value--text">
            {allergyAnamnesisLine || patient?.allergics?.value || "—"}
          </span>
        </div>
      ) : null}
    </div>
  );

  // Редактируемые поля плана (только в режиме редактирования):
  // название плана, статус и дата начала лечения. Показываются под шапкой
  // карточки плана — в самом заголовке название не редактируется.
  const planMetaEditor = changed ? (
    <div className="plan-meta-editor">
      <label className="plan-meta-editor__field plan-meta-editor__field--name">
        <span className="plan-meta-editor__label">
          {t("plan.header.nameLabel")}
        </span>
        <input
          className="form-control plan-meta-editor__input"
          id="plan-name-field"
          type="text"
          value={planName}
          onChange={(e) => setPlanName(e.target.value)}
          onBlur={handlePlanNameSave}
          onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
          placeholder={t("plan.list.defaultPlanName")}
        />
      </label>
      <label className="plan-meta-editor__field">
        <span className="plan-meta-editor__label">
          {t("plan.header.statusLabel")}
        </span>
        <select
          className="plan-status-select"
          value={planStatusValue}
          onChange={(e) => handlePlanStatusChange(e.target.value)}
          disabled={blocked}>
          <option value="active">{t("plan.header.statusActive")}</option>
          <option value="cancelled">{t("plan.header.statusArchived")}</option>
        </select>
      </label>
      <label className="plan-meta-editor__field">
        <span className="plan-meta-editor__label">
          {t("plan.header.startDateShort")}
        </span>
        <DateInput
          id="treatment-start-date"
          className="form-control"
          value={treatmentStartDate}
          name="treatmentStartDate"
          onChange={(e) => setTreatmentStartDate(e.target.value)}
          onBlur={handleTreatmentStartDateSave}
          onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
        />
      </label>
    </div>
  ) : null;

  // Отображаемое название плана: выводится в заголовке карточки плана
  // (и в группе, и в схеме). Редактируется отдельным полем ниже.
  const planTitle =
    patient?.result?.name?.value || t("plan.list.defaultPlanName");

  // Пропсы табличного представления плана. Разметка таблицы вынесена в
  // отдельный компонент PatientPlanTable, чтобы её правки не задевали
  // схему-матрицу (PatientPlanMatrix).
  const planTableProps = {
    groups: patient?.result?.groups || [],
    planId: patient?.result?.id?.value || 0,
    changed,
    blocked,
    coursesMode,
    courseSelected,
    addServiceMenuGroupId,
    setAddServiceMenuGroupId,
    courseIdToColor,
    courseIdToName,
    displayName,
    getGroupEffectiveTotal,
    getGroupAppointmentLimit,
    getAppointmentHeaderType,
    getAppointmentHeaderLabels,
    isWithoutAppointmentsGroupByName,
    getMarkedAppointmentsCountForItem,
    getAppointmentForItem,
    renderAppointmentStatus,
    fetchPlan,
    isSuccessResponse,
    logPlanChange,
    onDecrease: handleDecrease,
    onIncrease: handleIncrease,
    onColumnTypeChange: handleColumnTypeChange,
    onDeleteSubitem: handleDeleteSubitem,
    onChangeService: handleModalChangeServices,
    onAddSubitem: handleModalOpenSubitemServices,
    onDeleteService: handleDeleteService,
    onRemoveFromCourse: handleRemoveItemFromCourse,
    onToggleCourse: handleToggleCourseItem,
    onOpenAppointmentComment: handleOpenAppointmentCommentModal,
    onOpenPay: handleModalOpenPay,
    onDeleteGroup: handleDeleteGroup,
    onRequestAddGroup: loadAvailableGroups,
    onAddGroup: handleAddGroup,
    onOpenAddCourse: handleOpenModalAddCourse,
    onOpenAddServiceFromCatalog: handleModalOpenServices,
    onOpenAddCustomService: handleOpenModalCustomService,
    onDeletePlan: deletePlanHandler,
    deletePlanLabel: deletePlanIcon,
    viewSwitch: planViewSwitch,
    actions: planActionsNode,
    metaEditor: planMetaEditor,
    planTitle,
    t,
    locale,
  };

  return (
    <>
      {toast.title ? (
        <Toast key={toast.id} title={toast.title} status={toast.status} />
      ) : (
        false
      )}

      <div className="patient-plan">
          {/* Табы слева, «Вернуться в список» — справа (встроена в PlanTabs).
              Название плана живёт в заголовке карточки плана (card__title),
              поэтому отдельной полосы с заголовком здесь нет. */}
          <PlanTabs active="plan" />

          {(() => {
            // В режиме редактирования всегда показываем таблицу —
            // схема-матрица и таймлайн только для просмотра.
            const groupsBlock =
              activeViewMode === "timeline" ? (
                <PatientPlanTimeline
                  admissions={patient?.result?.admission || {}}
                  appointmentTypes={patient?.result?.appointment_types || []}
                  busy={blocked}
                  deletePlanLabel={deletePlanIcon}
                  groups={patient?.result?.groups || []}
                  onCompleteStage={handleTimelineCompleteStage}
                  onDeletePlan={deletePlanHandler}
                  planId={patient?.result?.id?.value}
                  planTitle={planTitle}
                  actions={planActionsNode}
                  viewSwitch={planViewSwitch}
                />
              ) : activeViewMode === "matrix" ? (
                <PatientPlanMatrix
                  groups={patient?.result?.groups || []}
                  planCountAppointment={patient?.result?.count_appointment?.value}
                  planId={patient?.result?.id?.value}
                  appointmentTypes={patient?.result?.appointment_types || []}
                  onRequestAddGroup={loadAvailableGroups}
                  onAddGroup={handleAddGroup}
                  onAddStage={handleAddStage}
                  onChangeStageType={handleChangeStageType}
                  onRemoveStage={handleRemoveStage}
                  onAddService={handleOpenAddServiceFromStage}
                  onAddCustomService={handleOpenAddCustomServiceFromStage}
                  addingStage={blocked}
                  onDeletePlan={deletePlanHandler}
                  deletePlanLabel={deletePlanIcon}
                  viewSwitch={planViewSwitch}
                  actions={planActionsNode}
                  planTitle={planTitle}
                />
              ) : (
                <PatientPlanTable {...planTableProps} />
              );
            // Метрики — ОТДЕЛЬНЫМ рядом карточек вне карточки плана
            // (как .pov-metrics в обзоре пациента), затем сам план.
            const content = (
              <>
                {changed ? null : planMetrics}
                {groupsBlock}
              </>
            );
            return planGroupsSlotEl
              ? createPortal(content, planGroupsSlotEl)
              : content;
          })()}
      </div>

      <SearchSelectModal
        show={showModalServices}
        onHide={() => {
          setShowModalServices(false);
          setSelectedServiceId("");
          setServiceSelectMode("add_service");
          setSelectedParentItemId(null);
          setPendingStageNumber(null);
          setServiceSearchQuery("");
          setServiceSearchResults([]);
          setServiceSearchError(null);
        }}
        title={serviceSelectMode === "add_subitem" ? t("plan.modals.selectSubitem") : t("plan.modals.selectService")}
        headerContent={
          serviceFilials.length > 0 ? (
            <Form.Group controlId="serviceFilialFilter">
              <Form.Label className="mb-1">
                {t("plan.modals.branchLabel")}
              </Form.Label>
              <Form.Select
                value={serviceClinicId}
                onChange={(e) => handleServiceClinicChange(e.target.value)}>
                <option value="">{t("plan.modals.allBranches")}</option>
                {serviceFilials.map((clinic) => (
                  <option key={clinic.id} value={String(clinic.id)}>
                    {clinic.name ||
                      t("plan.modals.branchFallback", { id: clinic.id })}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          ) : null
        }
        query={serviceSearchQuery}
        onQueryChange={setServiceSearchQuery}
        loading={serviceSearchLoading}
        options={serviceSearchResults}
        onSelect={serviceSelectMode === "add_subitem" ? handleAddSubitem : handleAddService}
        optionKey={(s) => String(s.id)}
        optionLabel={(s) =>
          pickLocalizedValue(
            { name: { value: s.name }, name_en: { value: s.name_en } },
            "name",
            i18n.language,
          )
        }
        optionSubLabel={(s) => s.composition}
        minQueryLength={2}
        hint={serviceSearchError ? t("plan.modals.searchError") : null}
        emptyMessage={t("plan.modals.nothingFound")}
        loadingMessage={t("plan.modals.searching")}
      />
      <EditableModal
        show={showModalService}
        onHide={() => setShowService(false)}
        title={t("plan.modals.editService")}
        initialData={paramsService}
        fields={fields}
        onSave={handleSaveParamsService}
      />

      <EditableModal
        show={showSetDateModal}
        onHide={() => {
          setShowSetDateModal(false);
          setSetDateParams({ appointmentNumber: null, groupId: null });
        }}
        title={t("plan.modals.setPlanDate")}
        fields={[
          {
            name: "date",
            label: t("plan.modals.planDateField"),
            type: "date",
          },
        ]}
        initialData={{ date: "" }}
        onSave={handleSaveAdmissionDate}
      />

      <EditableModal
        show={showAppointmentCommentModal}
        onHide={() => {
          setShowAppointmentCommentModal(false);
          setAppointmentCommentParams({ appointmentNumber: null, groupId: null, itemId: null });
          setAppointmentCommentValue("");
        }}
        title={t("plan.modals.appointmentNote")}
        fields={[
          {
            name: "comment",
            label: t("plan.modals.noteField"),
            type: "textarea",
          },
        ]}
        initialData={{ comment: appointmentCommentValue }}
        onSave={handleSaveAppointmentComment}
      />

      <Modal
        show={showAppointmentCommentViewModal}
        onHide={() => {
          setShowAppointmentCommentViewModal(false);
          setAppointmentCommentViewValue("");
        }}
        centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("plan.modals.appointmentNote")}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ whiteSpace: "pre-wrap" }}>
          {appointmentCommentViewValue || t("plan.modals.noteEmpty")}
        </Modal.Body>
      </Modal>

      <PaidModal
        show={showModalPay}
        onHide={() => setShowModalPay(false)}
        clinics={availableClinics}
        staff={payModalStaff}
        patientId={patientId}
        treatmentPlanId={paymentGroupId}
        defaultClinicId={userClinicId}
        onSave={handleSavePay}
      />

      {/* Модальное окно «Добавить курс» */}
      <Modal
        show={showModalAddCourse}
        onHide={() => setShowModalAddCourse(false)}
        centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("plan.modals.addCourseTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">{t("plan.modals.courseName")}</label>
            <input
              type="text"
              className="form-control"
              value={courseSelectedName}
              onChange={(e) => setCourseSelectedName(e.target.value)}
              placeholder={t("plan.modals.courseNamePlaceholder")}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">{t("plan.modals.courseColor")}</label>
            <select
              className="form-select"
              value={courseSelectedColor}
              onChange={(e) => setCourseSelectedColor(e.target.value)}>
              {COURSE_COLOR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">{t("plan.modals.courseSelected")}</label>
            <ul className="list-unstyled mb-0">
              {courseSelected.length === 0 ? (
                <li className="text-muted">{t("plan.modals.courseNoSelected")}</li>
              ) : (
                courseSelected.map((id) => {
                  const item = (patient?.result?.groups || [])
                    .flatMap((g) => g.items || [])
                    .find((it) => it?.id?.value === id);
                  const name = displayName(item) || id;
                  return <li key={id}>{name}</li>;
                })
              )}
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalAddCourse(false)}>
            {t("actions.cancel", { ns: "common" })}
          </Button>
          <Button variant="primary" onClick={handleSubmitAddCourse}>
            {t("plan.modals.createCourse")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно «Новая услуга» (только в план лечения, без каталога) */}
      <Modal
        show={showModalCustomService}
        onHide={() => {
          setShowModalCustomService(false);
          setCustomServiceName("");
          setCustomServicePrice("");
          setCustomServiceContext({
            groupType: null,
            groupId: null,
            appointmentNumber: null,
          });
        }}
        centered
        className="plan-custom-service-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t("plan.modals.addCustomServiceTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="plan-custom-service-field">
            <label className="form-label" htmlFor="custom-service-name">
              {t("plan.modals.customServiceName")}
            </label>
            <input
              id="custom-service-name"
              type="text"
              className="form-control"
              value={customServiceName}
              onChange={(e) => setCustomServiceName(e.target.value)}
              placeholder={t("plan.modals.customServiceNamePlaceholder")}
              autoFocus
            />
          </div>
          <div className="plan-custom-service-field">
            <label className="form-label" htmlFor="custom-service-price">
              {t("plan.modals.customServicePrice")}
            </label>
            <input
              id="custom-service-price"
              type="text"
              inputMode="decimal"
              className="form-control"
              value={customServicePrice}
              onChange={(e) => setCustomServicePrice(e.target.value)}
              placeholder={t("plan.modals.customServicePricePlaceholder")}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowModalCustomService(false);
              setCustomServiceName("");
              setCustomServicePrice("");
            }}>
            {t("actions.cancel", { ns: "common" })}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitCustomService}
            disabled={!customServiceName.trim()}>
            {t("plan.modals.createService")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default PatientPlan;
